import { useCallback } from 'react';
import { useMutation } from '@ts-core/lib/apollo';

import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';
import { removeItemFromCache } from '@ts-core/core/utils/apolloUtils';
import { useListReorder } from '@ts-core/lib/apollo';

import { getExercisesQuery, GetExercisesQueryResult, GetExercisesQueryVariables } from '../graphql/queries/getExercisesQuery';
import {
  toggleExercisePracticeMutation,
  ToggleExercisePracticeMutationResult,
  ToggleExercisePracticeMutationVariables,
} from '../graphql/mutations/toggleExercisePracticeMutation';
import { removeExerciseMutation, RemoveExerciseMutationResult, RemoveExerciseMutationVariables } from '../graphql/mutations/removeExerciseMutation';
import { sortExercisesMutation, SortExercisesMutationVariables } from '../graphql/mutations/sortExercisesMutation';
import {
  importExercisesFromCourseMutation,
  ImportExercisesFromCourseMutationVariables,
} from '../graphql/mutations/importExercisesFromCourseMutation';

import { Exercise } from '../types';

interface ExerciseActionsHookInput {
  courseId: string;
  subjectId: string;
}

interface ExerciseActionsHookOutput {
  onToggleExercisePractice: (exercise: Exercise) => void;

  onRemoveExercise: (exercise: Exercise) => void;

  onSortExercises: (from: number, to: number) => void;

  onImportExercises: (exercises: Exercise[], onSuccess?: () => void) => void;
  importExercisesLoading: boolean;
}

export default function useExerciseActions(input: ExerciseActionsHookInput): ExerciseActionsHookOutput {
  const { courseId, subjectId } = input;
  const { showErrorNotification } = useSnackbarNotifications();

  /**
   * Remove exercise
   */
  const [removeExercise] = useMutation<RemoveExerciseMutationResult, RemoveExerciseMutationVariables>(removeExerciseMutation, {
    onError: showErrorNotification,
    update: removeItemFromCache<RemoveExerciseMutationResult, GetExercisesQueryResult, GetExercisesQueryVariables>({
      query: getExercisesQuery,
      queryVariables: { courseId, subjectId },
      queryName: 'exercises',
      mutationName: 'exercise',
      idAccessor: 'id',
    }),
  });
  const onRemoveExercise = useCallback(
    (exercise: Exercise) => {
      removeExercise({
        variables: {
          courseId,
          subjectId,
          exerciseId: exercise.id,
        },
        optimisticResponse: {
          exercise,
        },
      });
    },
    [courseId, subjectId, removeExercise]
  );

  /**
   * Toggle Practice
   */
  const [toggleExercisePractice] = useMutation<ToggleExercisePracticeMutationResult, ToggleExercisePracticeMutationVariables>(
    toggleExercisePracticeMutation,
    {
      onError: showErrorNotification,
    }
  );
  const onToggleExercisePractice = useCallback(
    (exercise: Exercise) => {
      const updatedExercise = {
        ...exercise,
        forPractice: !exercise.forPractice,
      };

      toggleExercisePractice({
        variables: {
          courseId,
          subjectId,
          exerciseId: exercise.id,
          action: updatedExercise.forPractice ? 'enable-practice' : 'disable-practice',
        },
        optimisticResponse: {
          exercise: updatedExercise,
        },
      });
    },
    [courseId, subjectId, toggleExercisePractice]
  );

  /**
   * Sort exercises
   */
  const [sortExercises] = useMutation<any, SortExercisesMutationVariables>(sortExercisesMutation, { onError: showErrorNotification });
  const { onReorderItems: onSortExercises } = useListReorder<GetExercisesQueryResult, GetExercisesQueryVariables>({
    query: getExercisesQuery,
    queryVariables: { courseId, subjectId },
    queryName: 'exercises',
    onCallMutation: (from, to) => {
      sortExercises({
        variables: {
          courseId,
          subjectId,
          input: { from, to },
        },
      });
    },
  });

  /**
   * Import exercises
   */
  const [importExercises, { loading: importExercisesLoading }] = useMutation<any, ImportExercisesFromCourseMutationVariables>(
    importExercisesFromCourseMutation,
    { onError: showErrorNotification, refetchQueries: [{ query: getExercisesQuery, variables: { courseId, subjectId } }], awaitRefetchQueries: true }
  );
  const onImportExercises = useCallback(
    (exercises: Exercise[], onSuccess?: () => void) => {
      importExercises({
        variables: {
          courseId,
          subjectId,
          input: { exercises: exercises.map((e) => e.id) },
        },
        onCompleted: onSuccess,
      });
    },
    [importExercises, courseId, subjectId]
  );

  return {
    onToggleExercisePractice,
    onRemoveExercise,
    onSortExercises,

    onImportExercises,
    importExercisesLoading,
  };
}
